import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabaseClient';

const SIGNED_URL_TTL_SEC = 3600;
const THUMBNAIL_TRANSFORM = { width: 384, height: 384, resize: 'cover' };

/**
 * useSitePhotos
 * Reads photos for a session from the site_photos_v view (which projects
 * geog to plain lat/lng), then batch-signs thumbnail URLs for display.
 *
 * Writes (update/delete) go to the table directly via the returned helpers.
 * Storage cleanup on delete is handled here so callers don't need to know.
 */
export default function useSitePhotos(sessionId) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!sessionId) {
      setPhotos([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const { data: rows, error: selectErr } = await supabase
        .from('site_photos_v')
        .select('*')
        .eq('session_id', sessionId)
        .order('taken_at', { ascending: false });

      if (selectErr) throw selectErr;

      const paths = (rows ?? []).map((r) => r.storage_path);
      let urlByPath = new Map();

      if (paths.length > 0) {
        const { data: signed, error: signErr } = await supabase
          .storage
          .from('site-photos')
          .createSignedUrls(paths, SIGNED_URL_TTL_SEC, { transform: THUMBNAIL_TRANSFORM });

        if (signErr) throw signErr;
        for (const item of signed ?? []) {
          if (item.signedUrl) urlByPath.set(item.path, item.signedUrl);
        }
      }

      const hydrated = (rows ?? []).map((r) => ({
        ...r,
        thumbnailUrl: urlByPath.get(r.storage_path) ?? null,
      }));

      setPhotos(hydrated);
    } catch (err) {
      console.error('[useSitePhotos] load failed', err);
      setError(err.message || 'Failed to load photos');
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    load();
  }, [load]);

  const updatePhoto = useCallback(async (id, patch) => {
    const { error: updErr } = await supabase
      .from('site_photos')
      .update(patch)
      .eq('id', id);
    if (updErr) throw updErr;
    await load();
  }, [load]);

  const deletePhoto = useCallback(async (photo) => {
    if (photo?.storage_path) {
      await supabase.storage.from('site-photos').remove([photo.storage_path]);
    }
    const { error: delErr } = await supabase
      .from('site_photos')
      .delete()
      .eq('id', photo.id);
    if (delErr) throw delErr;
    await load();
  }, [load]);

  const getOriginalSignedUrl = useCallback(async (storagePath) => {
    const { data, error: signErr } = await supabase
      .storage
      .from('site-photos')
      .createSignedUrl(storagePath, SIGNED_URL_TTL_SEC);
    if (signErr) throw signErr;
    return data?.signedUrl ?? null;
  }, []);

  return {
    photos,
    loading,
    error,
    reload: load,
    updatePhoto,
    deletePhoto,
    getOriginalSignedUrl,
  };
}
